import { el, clear } from '../../core/dom.js';
import { api } from '../../core/api.js';
import { button } from '../../components/button.js';
import { errorPanel } from '../../components/error.js';
import { toast } from '../../components/toast.js';
import { SEARCH_WAIT, later, mergeRows } from './paging.js';

/**
 * Standing instructions (memory_type instruction).
 * @param {{
 *   rows: object[], total: number, browseRows: object[], browseTotal: number,
 *   query: string, search: { rows: object[], total: number } | null,
 *   status: string, error: string, canEdit: boolean, productId: string, openEditor: boolean,
 *   onPage: (spec: { q: string, offset: number, append: boolean }) => Promise<{ ok: boolean, error?: string, rows: object[], total: number }>,
 *   onRemember: (q: string, search: { rows: object[], total: number } | null) => void,
 *   onSaved: (memory: object) => void, onDeleted: (id: string) => void, onRefresh: () => void,
 * }} opts
 */
export function renderInstructions(opts) {
  const section = el('section', { class: 'kb-glass kb-instructions kb-rv kb-rv-3', id: 'kb-instructions' });
  let editing = opts.openEditor ? { id: '' } : null;
  let query = opts.query || '';
  let searching = Boolean(opts.search);
  let browseRows = (opts.browseRows || opts.rows || []).slice();
  let browseTotal = opts.browseTotal != null ? opts.browseTotal : opts.total;
  let rows = searching && opts.search ? opts.search.rows.slice() : browseRows.slice();
  let total = searching && opts.search ? opts.search.total : browseTotal;
  let busy = false;
  let ticket = 0;
  const listHost = el('div');
  const searchBox = el('input', {
    class: 'kb-search',
    type: 'search',
    placeholder: 'Search instructions',
    'aria-label': 'Search instructions',
    value: query,
    onInput(event) {
      query = event.currentTarget.value;
      runSearch();
    },
  });
  const runSearch = later(() => lookup(query), SEARCH_WAIT);
  draw();
  return section;

  function draw() {
    clear(section);
    if (opts.status === 'error') {
      section.append(instructionError(opts));
      return;
    }
    section.append(heading());
    if (!editing && browseTotal > 0) section.append(searchBox);
    if (editing) section.append(editor());
    else section.append(listHost);
    paintList();
    if (editing) focusInput();
  }

  function heading() {
    const showEmpty = !editing && !searching && !browseRows.length;
    return el('div', { class: 'kb-instr-top' },
      el('div', null,
        el('div', { class: 'kb-kicker' }, 'Your instructions ', el('span', { class: 'kb-chip' }, 'Always applied')),
        showEmpty ? emptyCopy() : null),
      !editing && opts.canEdit
        ? el('button', { class: 'kb-btn', type: 'button', onClick: openNew }, '+ Add instruction')
        : null);
  }

  function paintList() {
    if (editing) return;
    const nodes = [];
    if (!rows.length) {
      nodes.push(searching
        ? statusLine('No instructions match', 'Try a different search.')
        : null);
    } else {
      nodes.push(el('div', { class: 'kb-instr-list' }, rows.map((row) => instructionRow(row))));
    }
    if (rows.length < total) nodes.push(moreButton());
    listHost.replaceChildren(...nodes.filter(Boolean));
  }

  function moreButton() {
    return el('div', { class: 'kb-more' }, el('button', {
      class: 'kb-link',
      type: 'button',
      disabled: busy ? true : null,
      onClick: () => more(),
    }, busy ? 'Loading…' : 'Load more'));
  }

  function instructionRow(row) {
    return el('div', { class: 'kb-instr-row' },
      el('p', { class: 'kb-instr' }, row.content || ''),
      opts.canEdit
        ? el('button', {
          class: 'kb-btn',
          type: 'button',
          onClick() { editing = { id: row.id }; draw(); },
        }, 'Edit')
        : null);
  }

  function editor() {
    const source = browseRows.concat(rows);
    const current = source.find((row) => row.id === editing.id) || null;
    const input = el('textarea', {
      class: 'kb-instr-input',
      rows: '3',
      placeholder: 'Add a standing instruction…',
      onInput() { grow(input); },
    }, current ? current.content || '' : '');
    const actions = el('div', { class: 'kb-editor-actions' },
      el('button', {
        class: 'kb-btn kb-btn-fill',
        type: 'button',
        onClick: () => persist(input, current),
      }, 'Save'),
      el('button', {
        class: 'kb-btn',
        type: 'button',
        onClick() { editing = null; draw(); },
      }, 'Cancel'));
    if (current) {
      actions.append(el('button', {
        class: 'kb-btn kb-remove',
        type: 'button',
        onClick: () => remove(current),
      }, 'Remove'));
    }
    Promise.resolve().then(() => grow(input));
    return el('div', { class: 'kb-editor' }, input, actions);
  }

  function openNew() {
    editing = { id: '' };
    draw();
  }

  function focusInput() {
    Promise.resolve().then(() => section.querySelector('.kb-instr-input')?.focus());
  }

  async function lookup(q) {
    const mine = ++ticket;
    const text = q.trim();
    if (!text) {
      searching = false;
      rows = browseRows;
      total = browseTotal;
      opts.onRemember('', null);
      paintList();
      draw();
      return;
    }
    const page = await opts.onPage({ q: text, offset: 0, append: false });
    if (mine !== ticket || !section.isConnected) return;
    if (!page.ok) {
      toast(page.error || 'Could not search instructions', 'error');
      return;
    }
    searching = true;
    rows = page.rows;
    total = page.total;
    opts.onRemember(text, { rows, total });
    paintList();
  }

  async function more() {
    if (busy) return;
    busy = true;
    paintList();
    const page = await opts.onPage({
      q: searching ? query.trim() : '',
      offset: rows.length,
      append: true,
    });
    busy = false;
    if (!section.isConnected) return;
    if (!page.ok) {
      toast(page.error || 'Could not load instructions', 'error');
      paintList();
      return;
    }
    if (searching) {
      rows = mergeRows(rows, page.rows);
      total = page.total;
      opts.onRemember(query.trim(), { rows, total });
    } else {
      browseRows = mergeRows(browseRows, page.rows);
      browseTotal = page.total;
      rows = browseRows;
      total = browseTotal;
    }
    paintList();
  }

  async function persist(input, current) {
    const content = input.value.trim();
    if (!content) {
      toast('Write an instruction first', 'error');
      return;
    }
    const result = current
      ? await api(`/context/memory/${encodeURIComponent(current.id)}`, { method: 'PUT', body: { content } })
      : await api('/context/memory', {
        method: 'POST',
        body: { product_id: opts.productId, content, memory_type: 'instruction', title: 'instruction' },
      });
    if (result && result.error) {
      toast(result.error, 'error');
      return;
    }
    toast('Instructions saved');
    if (result.memory && opts.onSaved) opts.onSaved(result.memory);
    opts.onRefresh();
  }

  async function remove(current) {
    const result = await api(`/context/memory/${encodeURIComponent(current.id)}`, { method: 'DELETE' });
    if (result && result.error) {
      toast(result.error, 'error');
      return;
    }
    toast('Instruction removed');
    if (opts.onDeleted) opts.onDeleted(current.id);
    opts.onRefresh();
  }
}

function emptyCopy() {
  return el('p', { class: 'kb-copy' }, 'No standing instructions. Bonnie will answer from documents and memory alone.');
}

function statusLine(title, body) {
  return el('div', { class: 'kb-status' },
    el('p', { class: 'kb-instr' }, title),
    body ? el('p', { class: 'kb-note' }, body) : null);
}

function grow(input) {
  input.style.height = 'auto';
  input.style.height = `${Math.max(input.scrollHeight, 72)}px`;
}

export function instructionError(opts) {
  return errorPanel({
    title: 'Instructions could not be loaded',
    message: opts.error || 'Try again.',
    action: button('Retry', { onClick: opts.onRefresh }),
  });
}
